import { v4 as uuidv4 } from 'uuid';
import { Product, ProductWithPlanFields } from 'types/product';
import { PlanProductLineItem } from '../../../types';

/**
 * FR-091: a unique id for one *line item*, distinct from `productId` (the
 * catalog product it references), so the same product can be added to a
 * Plan more than once as independent entries. Unlike Applications/Work
 * Orders' `generateProductApplicationId` (a `productId_timestamp_random`
 * string, never sent to that backend), Plans' `trackingId` is persisted and
 * the backend validates it as a UUID — so this must return one.
 */
export const generateTrackingId = (): string => uuidv4();

/**
 * Adapts a Plan line item to Applications' `Product` shape for
 * `FillProductDetailsDrawer` (tank fields simply stay unset). `isExisting`
 * must be true on edit — `ProductCostSection` uses it to avoid overwriting a
 * saved price/cost with the catalog's default.
 */
export const mapPlanProductToApplicationsProduct = (
  item: PlanProductLineItem,
  isExisting: boolean
): ProductWithPlanFields =>
  ({
    id: item.productId,
    productApplicationId: item.trackingId || item.productId,
    name: item.productName,
    manufacturer: item.manufacturerName,
    ratePerAcre: item.productRateValue,
    ratePerAreaUnit: item.productRateUnit,
    totalProduct: item.totalProductValue,
    totalProductUnit: item.totalProductUnit,
    priceValue: item.specificCostValue,
    priceUnit: item.specificCostUnit,
    totalCost: item.totalProductCost,
    appliedAreaValue: item.areaValue,
    coveragePercent: item.coveragePercent,
    density: item.density,
    stdpackageunit: item.stdpackageunit,
    stdunit: item.stdunit,
    stdfactor: item.stdfactor,
    custom: item.custom,
    isInUse: isExisting,
    // A Plan product can be applied more than once per season, unlike an
    // Application (always a single event) — `ProductOtherSection` only
    // renders/edits this when `FillProductDetailsDrawer` is opened with
    // `showApplicationCount` (see `ProductsStep.tsx`).
    applicationCount: item.applicationCount,
    timingEvent: item.timingEvent,
    timingEventTag: item.timingEventTag,
    targetDateEpoch: item.targetDateEpoch,
    isCarrier: false,
    isHidden: false
  } as ProductWithPlanFields);

/**
 * Merges `FillProductDetailsDrawer`'s computed `Product` back onto the
 * existing (or stub, freshly-added) `PlanProductLineItem` — only the fields
 * the drawer's quantity/cost sections actually compute are overlaid;
 * Plans-only fields (`trackingId`, `pricingStrategy`, `currency`) are
 * preserved from the source line item, since Applications' `Product` type
 * has no equivalent for them. `applicationCount` **is** overlaid — Plans is
 * the one place `ProductOtherSection`'s "# of Applications" input actually
 * renders and computes it (`showApplicationCount`), so the drawer's value is
 * the source of truth once the drawer has been open.
 */
export const mergeApplicationsProductIntoPlanLineItem = (
  existing: PlanProductLineItem,
  product: ProductWithPlanFields
): PlanProductLineItem => ({
  ...existing,
  productRateValue:
    typeof product.ratePerAcre === 'number'
      ? product.ratePerAcre
      : existing.productRateValue,
  productRateUnit: product.ratePerAreaUnit ?? existing.productRateUnit,
  totalProductValue:
    typeof product.totalProduct === 'number'
      ? product.totalProduct
      : existing.totalProductValue,
  totalProductUnit: product.totalProductUnit ?? existing.totalProductUnit,
  areaValue: product.appliedAreaValue ?? existing.areaValue,
  specificCostValue: product.priceValue ?? existing.specificCostValue,
  specificCostUnit: product.priceUnit ?? existing.specificCostUnit,
  totalProductCost: product.totalCost ?? existing.totalProductCost,
  coveragePercent: product.coveragePercent ?? existing.coveragePercent,
  applicationCount: product.applicationCount ?? existing.applicationCount,
  timingEvent: product.timingEvent ?? existing.timingEvent,
  timingEventTag: product.timingEventTag ?? existing.timingEventTag,
  targetDateEpoch: product.targetDateEpoch,
  custom: product.custom ?? existing.custom,
  saveAsNewListPrice: product.saveAsNewListPrice ?? existing.saveAsNewListPrice
});

/**
 * `CreateCustomProductDrawer`'s `onSuccess` also returns an Applications
 * `Product` (the newly created inventory item, pre-fill-details) — this
 * builds the Plans stub it's merged onto before opening
 * `FillProductDetailsDrawer` for it, mirroring `mapProductToAdd`'s 'plans'
 * branch defaults for the fields a fresh custom product doesn't carry yet.
 */
export const buildPlanLineItemStubFromCustomProduct = (
  product: Product,
  area: number
): PlanProductLineItem => ({
  productId: product.id || '',
  productName: product.name || '',
  density: product.density,
  trackingId: generateTrackingId(),
  // null (not 0) — see `mapProductToAdd`'s 'plans' branch.
  productRateValue: null,
  productRateUnit: product.stdunit || '',
  areaValue: area,
  areaUnit: 'acre',
  totalProductValue: null,
  totalProductUnit: product.stdunit || '',
  pricingStrategy: 'SystemDefined',
  specificCostValue: 0,
  specificCostUnit: product.stdunit || '',
  totalProductCost: 0,
  coveragePercent: 100,
  applicationCount: 1,
  manufacturerName: product.manufacturer || '',
  productDensity: product.density,
  producttype: product.productType || '',
  registrationnumber:
    product.registrationNumber || product.registrationnumber || '',
  stdfactor: product.stdfactor || 1,
  stdpackageunit: product.stdpackageunit || '',
  stdunit: product.stdunit || '',
  currency: 'USD',
  custom: true
});
